import {
  ChangeDetectionStrategy, Component, ElementRef, HostListener, effect, signal, viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { WhatsNewEntry } from '../../models/whats-new.model';
import { WhatsNewService } from '../../services/whats-new.service';
import { GuidedTourService } from '../../services/guided-tour.service';
import { HubDestinationsService } from '../../services/hub-destinations.service';
import { AGENT_HUB_TOUR } from '../../config/agent-hub-tour';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * The What's New panel — a small, Cursor-style floating card anchored to the
 * bottom-left (no backdrop; the app stays usable behind it). Mounted once in
 * the layout shell (like AbilitiesModalComponent) and driven entirely by
 * WhatsNewService, so any surface can open it and the home screen can
 * auto-open it. Content, release label, banner theme, and the optional
 * changelog link are admin-authored (Admin portal → GET /agenthome/whats-new);
 * this component only renders and executes entry CTAs (a row with a cta is
 * clickable). The header is a curated gradient banner ([data-theme] styles) —
 * the release label renders as a chip on it, never inside the title sentence.
 *
 * Layout: an admin may flag ONE entry as featured — it takes the spotlight at
 * the top (optional artwork, larger type, the CTA promoted to a button) and
 * everything else renders as a compact digest beneath, so a launch never has to
 * compete with a copy tweak for the same line of title. With nothing flagged
 * the panel is exactly the flat list it has always been.
 *
 * Closing (× / Escape) does NOT mark anything seen — only "Got it" does. The
 * panel is non-modal by design, so focus is never trapped; it is moved into the
 * panel only when the user opened it deliberately, and restored on close.
 */
@Component({
  selector: 'app-whats-new-modal',
  standalone: true,
  templateUrl: './whats-new-modal.component.html',
  styleUrls: ['./whats-new-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WhatsNewModalComponent {
  private readonly panelRef = viewChild<ElementRef<HTMLElement>>('panel');
  /** What had focus before a deliberate open, so it can be handed back. */
  private returnFocusTo: HTMLElement | null = null;
  /** Entry ids whose artwork failed to load — the hero then renders text-only
   *  rather than reserving space for an image that will never arrive. */
  private readonly failedImages = signal<ReadonlySet<string>>(new Set());

  constructor(
    readonly whatsNew: WhatsNewService,
    private readonly tour: GuidedTourService,
    private readonly destinations: HubDestinationsService,
    private readonly router: Router,
  ) {
    // Focus management for a NON-modal dialog: never trap (the app stays
    // usable behind the panel), but a keyboard user who opened it on purpose
    // should land inside it rather than having to tab the whole page to reach
    // it. An auto-open is not a request for focus, so it never steals it.
    effect(() => {
      const open = this.whatsNew.open();
      const panel = this.panelRef()?.nativeElement;
      if (open && panel && this.whatsNew.showingAll()) {
        this.returnFocusTo = document.activeElement as HTMLElement | null;
        panel.focus({ preventScroll: true });
      } else if (!open && this.returnFocusTo) {
        this.returnFocusTo.focus?.({ preventScroll: true });
        this.returnFocusTo = null;
      }
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.whatsNew.open()) this.close();
  }

  /** × / Escape — put it away without consuming unread announcements. */
  close(): void {
    this.whatsNew.close();
  }

  /** True once this entry's artwork has 404'd (or been blocked). */
  heroImageBroken(id: string): boolean {
    return this.failedImages().has(id);
  }

  onHeroImageError(id: string): void {
    this.failedImages.update(set => new Set(set).add(id));
  }

  /** Banner subline: "3 updates · newest Jul 18". Empty when there is nothing
   *  to summarize (no entries, or none carry a ship date). */
  subtitle(): string {
    const entries = this.whatsNew.entries();
    if (entries.length === 0) return '';
    const count = `${entries.length} update${entries.length === 1 ? '' : 's'}`;
    const newest = this.shortDate(entries[0]?.date ?? '');
    return newest ? `${count} · newest ${newest}` : count;
  }

  /** 'YYYY-MM-DD' → 'Jul 18'; anything else → '' (renders nothing). */
  shortDate(iso: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((iso || '').trim());
    if (!m) return '';
    const month = MONTHS[Number(m[2]) - 1];
    return month ? `${month} ${Number(m[3])}` : '';
  }

  dismiss(): void {
    this.whatsNew.dismiss();
  }

  runCta(entry: WhatsNewEntry): void {
    const cta = entry.cta;
    if (!cta) return;
    // Acting on a CTA acknowledges THAT entry — the strongest read signal there
    // is — but says nothing about the others, so they survive to be read.
    this.whatsNew.acknowledgeOne(entry.id);
    this.whatsNew.close();
    // Every supported destination (routes + abilities/catalog/studio/admin) is
    // resolved by HubDestinationsService — the same set the Admin portal offers.
    this.destinations.go({ route: cta.route, action: cta.actionId });
  }

  /** Footer link: replay the home-screen walkthrough. Runs through the
   *  deferred-start hand-off so the home page can expand its sidebar (and we
   *  can navigate there first when opened from another surface). */
  takeTour(): void {
    // Leaving for the tour is not "I've read these" — close, don't consume.
    this.whatsNew.close();
    this.router.navigate(['/chat']);
    this.tour.requestStart(AGENT_HUB_TOUR);
  }

  openChangelog(): void {
    const url = this.whatsNew.changelogUrl();
    if (url) window.open(url, '_blank', 'noopener');
  }
}
